import type { Answer, PublicSlide } from "@engage/contracts";
import { useEffect, useState } from "preact/hooks";
import { useI18n } from "./i18n";
/**
 * Answering one slide. Every input is a tap or a short form: phones, in a hurry, in a
 * crowd. The server validates everything again; these checks only save a round trip.
 */
export function AnswerForm({
  slide,
  submit,
}: {
  slide: PublicSlide;
  submit: (answer: Answer) => Promise<string | null>;
}) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async (answer: Answer) => {
    if (busy) return;
    setBusy(true);
    setError(await submit(answer));
    setBusy(false);
  };

  // The answer tiles stand on the stage by themselves, as big as the screen allows;
  // everything typed or picked from a scale sits in a card.
  const tiles = slide.type === "mcq" || slide.type === "true_false" || slide.type === "poll";
  return (
    <div class={tiles ? "stack tiles" : "card stack"}>
      {renderInput(slide, busy, send, t)}
      {error && <p class="error" role="alert">{error}</p>}
    </div>
  );
}

/**
 * The question's class: smaller words for a longer question, so the answer tiles stay
 * on screen under it rather than below the fold.
 */
export function promptClass(prompt: string): string {
  return prompt.length > 180 ? "prompt longer" : prompt.length > 100 ? "prompt long" : "prompt";
}

/**
 * The tiles' class. Two across while every option fits a half-width tile; past that,
 * one full-width row each, as tall as its words, so no label spills into the next.
 */
function choicesClass(options: PublicSlide["options"]): string {
  const n = options.length;
  const longest = Math.max(0, ...options.map((o) => o.label.length));
  const long = longest > (n === 2 || n === 4 ? 28 : 44);
  return `choices n${n}${long ? " long" : ""}`;
}

function renderInput(
  slide: PublicSlide,
  busy: boolean,
  send: (a: Answer) => void,
  t: (k: string, v?: Record<string, string | number>) => string,
) {
  switch (slide.type) {
    case "mcq":
    case "true_false":
    case "poll":
      if (slide.multiSelect && slide.type !== "true_false") return <ChoicesInput slide={slide} busy={busy} send={send} t={t} />;
      return (
        <div class={choicesClass(slide.options)}>
          {slide.options.map((o, i) => (
            <button
              key={o.id}
              class={`choice c${i % 4}`}
              disabled={busy}
              onClick={() => send({ type: slide.type as "mcq", optionIds: [o.id] })}
            >
              <span class="letter" aria-hidden="true">
                {String.fromCharCode(65 + i)}
              </span>
              {o.label}
            </button>
          ))}
        </div>
      );
    case "scale":
      return <ScaleInput slide={slide} busy={busy} send={send} />;
    case "word_cloud":
      return <WordsInput max={slide.maxWords} busy={busy} send={send} t={t} />;
    case "open_text":
    case "type_answer":
      return <TextInput type={slide.type} busy={busy} send={send} t={t} />;
    default:
      return null;
  }
}

/**
 * Several options, then send: one tap no longer means "this one". Picks are toggled,
 * and the server holds the slide to its own setting whatever is sent.
 */
function ChoicesInput({
  slide,
  busy,
  send,
  t,
}: {
  slide: PublicSlide;
  busy: boolean;
  send: (a: Answer) => void;
  t: (k: string, v?: Record<string, string | number>) => string;
}) {
  const [picked, setPicked] = useState<string[]>([]);
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  return (
    <>
      <p class="muted center">{t("live.pickSeveral")}</p>
      <div class={choicesClass(slide.options)}>
        {slide.options.map((o, i) => (
          <button
            key={o.id}
            class={`choice c${i % 4}`}
            aria-pressed={picked.includes(o.id)}
            disabled={busy}
            onClick={() => toggle(o.id)}
          >
            <span class="letter" aria-hidden="true">
              {picked.includes(o.id) ? "✓" : String.fromCharCode(65 + i)}
            </span>
            {o.label}
          </button>
        ))}
      </div>
      <button
        class="primary"
        disabled={busy || picked.length === 0}
        // In the slide's order, not the order tapped: the same picks are the same answer.
        onClick={() =>
          send({ type: slide.type as "mcq", optionIds: slide.options.filter((o) => picked.includes(o.id)).map((o) => o.id) })
        }
      >
        {busy ? t("live.sending") : t("live.submit")}
      </button>
    </>
  );
}

function ScaleInput({ slide, busy, send }: { slide: PublicSlide; busy: boolean; send: (a: Answer) => void }) {
  const values = Array.from({ length: slide.scaleMax - slide.scaleMin + 1 }, (_, i) => slide.scaleMin + i);
  const [value, setValue] = useState(Math.round((slide.scaleMin + slide.scaleMax) / 2));
  const { t } = useI18n();

  // Buttons when they fit on a phone; a slider for wide ranges.
  if (values.length <= 11) {
    return (
      <div class="scale">
        {values.map((v) => (
          <button key={v} class="scale-step" disabled={busy} onClick={() => send({ type: "scale", value: v })}>
            {v}
          </button>
        ))}
      </div>
    );
  }
  return (
    <div class="stack">
      <input
        type="range"
        min={slide.scaleMin}
        max={slide.scaleMax}
        value={value}
        onInput={(e) => setValue(Number((e.target as HTMLInputElement).value))}
      />
      <p class="big center">{value}</p>
      <button class="primary" disabled={busy} onClick={() => send({ type: "scale", value })}>
        {busy ? t("live.sending") : t("live.submit")}
      </button>
    </div>
  );
}

function WordsInput({
  max,
  busy,
  send,
  t,
}: {
  max: number;
  busy: boolean;
  send: (a: Answer) => void;
  t: (k: string, v?: Record<string, string | number>) => string;
}) {
  const [words, setWords] = useState<string[]>(() => Array.from({ length: max }, () => ""));
  const filled = words.map((w) => w.trim()).filter(Boolean);
  return (
    <form
      class="stack"
      onSubmit={(e) => {
        e.preventDefault();
        if (filled.length > 0) send({ type: "word_cloud", words: filled });
      }}
    >
      {words.map((w, i) => (
        <input
          key={i}
          value={w}
          maxLength={40}
          placeholder={t("live.wordsPlaceholder", { n: i + 1 })}
          autoFocus={i === 0}
          onInput={(e) => {
            const next = [...words];
            next[i] = (e.target as HTMLInputElement).value;
            setWords(next);
          }}
        />
      ))}
      <button class="primary" type="submit" disabled={busy || filled.length === 0}>
        {busy ? t("live.sending") : t("live.submit")}
      </button>
    </form>
  );
}

function TextInput({
  type,
  busy,
  send,
  t,
}: {
  type: "open_text" | "type_answer";
  busy: boolean;
  send: (a: Answer) => void;
  t: (k: string, v?: Record<string, string | number>) => string;
}) {
  const [text, setText] = useState("");
  const max = type === "open_text" ? 500 : 200;
  return (
    <form
      class="stack"
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim()) send({ type, text: text.trim() });
      }}
    >
      <textarea
        value={text}
        maxLength={max}
        rows={type === "open_text" ? 4 : 2}
        placeholder={t("live.textPlaceholder")}
        onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
      />
      <button class="primary" type="submit" disabled={busy || !text.trim()}>
        {busy ? t("live.sending") : t("live.submit")}
      </button>
    </form>
  );
}

/**
 * Seconds left on this phone's corrected clock, in a ring that empties with them — the
 * projector's countdown, small. Display only; the server decides.
 */
export function Countdown({ deadline, limitMs }: { deadline: number | null; limitMs: number }) {
  const { t } = useI18n();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (deadline === null) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [deadline]);
  if (deadline === null) return null;
  const left = Math.max(0, deadline - now);
  const seconds = Math.ceil(left / 1000);
  const share = limitMs > 0 ? Math.min(1, left / limitMs) : 1;
  const around = 2 * Math.PI * 44;
  return (
    <span class={`countdown${seconds <= 5 ? " urgent" : ""}`} role="timer" aria-label={t("live.secondsLeft", { seconds })}>
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <circle class="track" cx="50" cy="50" r="44" />
        <circle class="left" cx="50" cy="50" r="44" stroke-dasharray={around} stroke-dashoffset={around * (1 - share)} />
      </svg>
      <span aria-hidden="true">{seconds}</span>
    </span>
  );
}
